/**
 * @lodex-pro/web-admin — a rate-card version's draft lines (Task 21.7b), as the editor holds
 * them and as the version PUT takes them. Pure, so the tests pin the rules directly.
 *
 * The checks mirror routes/platform/billing/rate-cards.ts so an admin sees a problem before
 * saving; the API checks everything again and stays the authority.
 */
import {
  formatCentsAsDollars,
  hasSubCentPrecision,
  parseDollarsToCents,
} from '@lodex-pro/shared/money';
import type { ChargeKind, RateCardLine, RateCardLineBody, VersionBody } from './billing-hooks';
import { formatDollarsFromCents } from '@lodex-pro/shared/number-format';

export const CHARGE_KINDS: readonly ChargeKind[] = [
  'FLAT_MONTHLY',
  'PER_UNIT',
  'TIERED_BAND',
  'PER_MODULE',
];

export const CHARGE_KIND_LABELS: Readonly<Record<ChargeKind, string>> = {
  FLAT_MONTHLY: 'Flat monthly',
  PER_UNIT: 'Per unit',
  TIERED_BAND: 'Tiered bands',
  PER_MODULE: 'Per module',
};

/** The usage meters (shared PLATFORM_BILLING_METERS), in words for the picker. */
export const METER_LABELS: Readonly<Record<string, string>> = {
  ACTIVE_DRIVERS: 'Active drivers',
  ACTIVE_TRUCKS: 'Active trucks',
  ACTIVE_USERS: 'Active users',
  API_CONNECTIONS: 'API connections',
  API_REQUESTS: 'API requests',
  MAP_LOADS: 'Map loads',
  HAULS_COMPLETED: 'Hauls completed',
  BBL_HAULED: 'Barrels hauled',
  PORTAL_USERS: 'Portal users (peak)',
  DISPOSAL_MODULES: 'Disposal modules enabled',
  COMPLIANCE_STATES: 'Compliance states',
};

export interface BandDraft {
  fromQty: string;
  toQty: string;
  price: string;
}

export interface LineDraft {
  code: string;
  description: string;
  chargeKind: ChargeKind;
  meter: string;
  moduleCode: string;
  unitPrice: string;
  includedQuantity: string;
  bands: BandDraft[];
  isAddOn: boolean;
}

export const emptyLine = (): LineDraft => ({
  code: '',
  description: '',
  chargeKind: 'FLAT_MONTHLY',
  meter: '',
  moduleCode: '',
  unitPrice: '',
  includedQuantity: '0',
  bands: [],
  isAddOn: false,
});

export const emptyBand = (): BandDraft => ({ fromQty: '', toQty: '', price: '' });

/** A saved line → the editor's strings (cents shown as dollars). */
export function lineToDraft(l: RateCardLine): LineDraft {
  return {
    code: l.code,
    description: l.description,
    chargeKind: l.chargeKind,
    meter: l.meter ?? '',
    moduleCode: l.moduleCode ?? '',
    unitPrice: formatCentsAsDollars(l.unitPriceCents),
    includedQuantity: l.includedQuantity,
    bands: (l.bands ?? []).map((b) => ({
      fromQty: String(b.fromQty),
      toQty: b.toQty === null ? '' : String(b.toQty),
      price: formatCentsAsDollars(b.priceCents),
    })),
    isAddOn: l.isAddOn,
  };
}

const CODE_RE = /^[A-Z0-9_]{1,40}$/;
const QTY_RE = /^\d{1,10}(\.\d{1,4})?$/;

const needsMeter = (k: ChargeKind): boolean => k === 'PER_UNIT' || k === 'TIERED_BAND';

/** A price as typed: an error, or null when it is a whole number of cents, 0 or more. */
export function priceError(raw: string): string | null {
  const p = parseDollarsToCents(raw);
  if (!p.ok) return p.reason === 'blank' ? 'Required' : 'Enter an amount, e.g. 12.50';
  if (p.cents < 0) return 'Cannot be negative';
  if (hasSubCentPrecision(raw)) return 'Whole cents only';
  return null;
}

/** Every problem in the draft, as "line N: …" messages; empty when it can be saved. */
export function draftErrors(lines: readonly LineDraft[]): string[] {
  const out: string[] = [];
  const codes = new Set<string>();
  lines.forEach((l, i) => {
    const at = `Line ${String(i + 1)}`;
    const code = l.code.trim().toUpperCase();
    if (!CODE_RE.test(code)) out.push(`${at}: the code must be letters, digits or underscore`);
    else if (codes.has(code)) out.push(`${at}: the code ${code} is used twice`);
    codes.add(code);
    if (l.description.trim() === '') out.push(`${at}: a description is required`);
    const pe = priceError(l.unitPrice);
    if (pe !== null) out.push(`${at}: unit price: ${pe}`);
    if (!QTY_RE.test(l.includedQuantity.trim())) {
      out.push(`${at}: included quantity must be a number with at most 4 decimals`);
    }
    if (needsMeter(l.chargeKind) && l.meter === '') out.push(`${at}: choose a meter`);
    if (l.chargeKind === 'PER_MODULE' && !CODE_RE.test(l.moduleCode.trim().toUpperCase())) {
      out.push(`${at}: a module code is required`);
    }
    if (l.chargeKind === 'TIERED_BAND') {
      if (l.bands.length === 0) out.push(`${at}: add at least one band`);
      let prevTo: number | null = null;
      l.bands.forEach((b, j) => {
        const bt = `${at}, band ${String(j + 1)}`;
        // Half-open [from, to) and contiguous, as the API and the pricing read them (#945 MED-2).
        // Quantities are plain decimals (QTY_RE): "0x10" or "1e3" are refused (#945 LOW-1).
        const fromOk = QTY_RE.test(b.fromQty.trim());
        const toBlank = b.toQty.trim() === '';
        const toOk = toBlank || QTY_RE.test(b.toQty.trim());
        const from = Number(b.fromQty);
        const to = toBlank ? null : Number(b.toQty);
        if (!fromOk) out.push(`${bt}: "from" must be a number, 0 or more`);
        if (!toOk) out.push(`${bt}: "to" must be a number`);
        if (fromOk && toOk && to !== null && to <= from) {
          out.push(`${bt}: "to" must be above "from"`);
        }
        if (to === null && j !== l.bands.length - 1) {
          out.push(`${bt}: only the last band may have no upper limit`);
        }
        // The first band starts at 0, so no usage falls below every band (#949 review LOW-2).
        if (j === 0 && fromOk && from !== 0) out.push(`${bt}: the first band must start at 0`);
        if (j > 0 && fromOk && prevTo !== null && from !== prevTo) {
          out.push(`${bt}: must start at ${String(prevTo)}, where the band before ends`);
        }
        prevTo = to;
        const bp = priceError(b.price);
        if (bp !== null) out.push(`${bt}: price: ${bp}`);
      });
    }
  });
  return out;
}

/** The PUT body for a valid draft. Only the fields each charge kind uses are sent. */
export function versionBody(
  effectiveFrom: string,
  notes: string,
  lines: readonly LineDraft[],
): VersionBody {
  return {
    effectiveFrom,
    notes: notes.trim() === '' ? null : notes.trim(),
    lines: lines.map(
      (l, i): RateCardLineBody => ({
        code: l.code.trim().toUpperCase(),
        description: l.description.trim(),
        chargeKind: l.chargeKind,
        meter: needsMeter(l.chargeKind) ? l.meter : null,
        moduleCode: l.chargeKind === 'PER_MODULE' ? l.moduleCode.trim().toUpperCase() : null,
        unitPrice: l.unitPrice.trim(),
        includedQuantity: l.includedQuantity.trim(),
        bands:
          l.chargeKind === 'TIERED_BAND'
            ? l.bands.map((b) => ({
                fromQty: Number(b.fromQty),
                toQty: b.toQty.trim() === '' ? null : Number(b.toQty),
                price: b.price.trim(),
              }))
            : null,
        isAddOn: l.isAddOn,
        sortOrder: i,
      }),
    ),
  };
}

/** A line's price in words for the read-only table. */
export function linePriceText(l: RateCardLine): string {
  const unit = formatDollarsFromCents(l.unitPriceCents);
  switch (l.chargeKind) {
    case 'FLAT_MONTHLY':
      return `${unit} a month`;
    case 'PER_MODULE':
      return `${unit} a month for ${l.moduleCode ?? 'the module'}`;
    case 'PER_UNIT': {
      const meter = METER_LABELS[l.meter ?? ''] ?? l.meter ?? '';
      const free = Number(l.includedQuantity) > 0 ? `, first ${l.includedQuantity} included` : '';
      return `${unit} per ${meter.toLowerCase()}${free}`;
    }
    case 'TIERED_BAND': {
      const bands = (l.bands ?? [])
        .map(
          (b) =>
            `${String(b.fromQty)}${b.toQty === null ? '+' : `–${String(b.toQty)}`}: $${formatCentsAsDollars(b.priceCents)}`,
        )
        .join('; ');
      return `${METER_LABELS[l.meter ?? ''] ?? l.meter ?? ''} bands: ${bands}`;
    }
  }
}
